import DeleteIcon from '@mui/icons-material/Delete'
import React from 'react'

import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'

import { styled } from '@mui/material/styles'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx, switchSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'
import { FilterExtraCboScope, NumberOperator } from '../../tcm_scopes'
import {
    FieldIconButton,
    FieldRow,
    FilterSwitchLabel,
    FilterSwitchRow
} from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('FilterExtraCbo')

const ExtraPanel = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 12,
    border: '1px solid var(--border)',
    borderRadius: 9,
    backgroundColor: 'var(--surface)'
})

type FilterExtraCboViewProps = {
    className?: string
    scope: FilterExtraCboScope
}

class FilterExtraCboViewClass implements FCClassContext<FilterExtraCboViewProps> {
    scope!: FilterExtraCboScope

    onSyncState({ scope }: FilterExtraCboViewProps) {
        this.scope = scope
    }

    render({ className, scope }: FilterExtraCboViewProps): React.JSX.Element {
        const isRange =
            scope.extraOperator === NumberOperator.in_range || scope.extraOperator === NumberOperator.out_of_range

        return (
            <>
                <FilterSwitchRow className={className}>
                    <Switch
                        size="small"
                        sx={switchSx}
                        checked={this.scope.isFilterQuantidade}
                        onChange={this.emitOnIsFiltroQuantidadeChanged}
                        slotProps={{
                            input: { 'aria-label': 'Filtrar quantidade por empresa' }
                        }}
                    />
                    <div>
                        <FilterSwitchLabel as="span">Filtrar quantidade por empresa</FilterSwitchLabel>
                        <FieldHelp>
                            Só entram as empresas cuja quantidade de profissionais atende ao critério abaixo.
                        </FieldHelp>
                    </div>
                </FilterSwitchRow>
                {this.scope.isFilterQuantidade && (
                    <ExtraPanel>
                        <Field>
                            <FieldLabel>Critério</FieldLabel>
                            <TextField
                                select
                                size="small"
                                defaultValue={`${NumberOperator.in_range}`}
                                value={`${scope.extraOperator}`}
                                onChange={this.handleSelectChange}
                                sx={inputSx}
                                fullWidth
                            >
                                <MenuItem value={NumberOperator.is_less_then}>Menor que</MenuItem>
                                <MenuItem value={NumberOperator.is_less_then_or_equal_to}>Menor que ou igual</MenuItem>
                                <MenuItem value={NumberOperator.is_greater_then}>Maior que</MenuItem>
                                <MenuItem value={NumberOperator.is_greater_then_or_equal_to}>
                                    Maior que ou igual
                                </MenuItem>
                                <MenuItem value={NumberOperator.is}>É igual</MenuItem>
                                <MenuItem value={NumberOperator.in_range}>No intervalo</MenuItem>
                            </TextField>
                        </Field>
                        {!isRange && (
                            <Field>
                                <FieldLabel>Valor</FieldLabel>
                                <TextField
                                    size="small"
                                    defaultValue={scope.extraValue}
                                    onChange={this.handleFromValueChange}
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <FieldIconButton
                                                    size="small"
                                                    aria-label="Limpar valor"
                                                    onClick={this.handleClearClick}
                                                >
                                                    <DeleteIcon />
                                                </FieldIconButton>
                                            )
                                        }
                                    }}
                                />
                            </Field>
                        )}
                        {isRange && (
                            <FieldRow>
                                <Field style={{ flexGrow: 1 }}>
                                    <FieldLabel>De</FieldLabel>
                                    <TextField
                                        size="small"
                                        defaultValue={scope.extraValue}
                                        onChange={this.handleFromValueChange}
                                        sx={inputSx}
                                        fullWidth
                                    />
                                </Field>
                                <Field style={{ flexGrow: 1 }}>
                                    <FieldLabel>Até</FieldLabel>
                                    <TextField
                                        size="small"
                                        defaultValue={scope.extraTo}
                                        onChange={this.handleToValueChange}
                                        sx={inputSx}
                                        fullWidth
                                    />
                                </Field>
                                <FieldIconButton
                                    size="small"
                                    aria-label="Limpar intervalo"
                                    onClick={this.handleClearClick}
                                >
                                    <DeleteIcon />
                                </FieldIconButton>
                            </FieldRow>
                        )}
                    </ExtraPanel>
                )}
            </>
        )
    }

    emitOnIsFiltroQuantidadeChanged = () => {
        this.scope.onChangeIsFiltroQuantidade(!this.scope.isFilterQuantidade).catch(LOG.caught)
    }

    readonly handleSelectChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onExtraOperatorChange(+event.target.value).catch(LOG.caught)
    }

    readonly handleFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onExtraValueChange(event.target.value, 'from').catch(LOG.caught)
    }

    readonly handleToValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onExtraValueChange(event.target.value, 'to').catch(LOG.caught)
    }

    readonly handleClearClick = () => {
        this.scope.onExtraClear().catch(LOG.caught)
    }
}

export const FilterExtraCboView = classToFComponent(FilterExtraCboViewClass, React)
export default FilterExtraCboView
